import type { GodPresentationEvent, GridPosition, SlotGrid } from '../rendering/types';

export interface ObeliskVisualCell extends GridPosition {
  readonly multiplier: number;
}

export interface ObeliskVisualPlan {
  readonly reel: number;
  readonly reels: readonly number[];
  readonly level: number;
  readonly mode: 'wild-reel' | 'full-grid-multiplier';
  readonly cells: readonly ObeliskVisualCell[];
}

function samePosition(left: GridPosition, right: GridPosition): boolean {
  return left.reel === right.reel && left.row === right.row;
}

/**
 * Creates the display-only footprint of an already evaluated Obelisk event.
 * It never changes symbols, wins or sticky state. In particular, Scatter and
 * the Pot-of-Greed blocker remain visible exactly as they were evaluated.
 */
export function createObeliskVisualPlan(
  event: GodPresentationEvent,
  grid: SlotGrid,
  sticky: readonly GridPosition[],
  blocked: GridPosition | null,
): ObeliskVisualPlan | null {
  if (event.god !== 'OBELISK' || event.obeliskReel === undefined || event.obeliskMode === undefined) return null;
  const reel = event.obeliskReel;
  const footprint = event.obeliskCells ?? [
    { reel, row: 0 }, { reel, row: 1 }, { reel, row: 2 },
  ];
  const cells: ObeliskVisualCell[] = [];
  for (const position of footprint) {
    const untouchedScatter = event.obeliskMode === 'wild-reel' && grid[position.reel]?.[position.row] === 'SCATTER';
    if (untouchedScatter || (blocked !== null && samePosition(position, blocked))) continue;
    const existing = sticky.find((entry) => samePosition(entry, position));
    const permanentMultiplier = existing?.multiplier ?? 1;
    cells.push(Object.freeze({
      ...position,
      // A regular transformed symbol is a temporary 1x wild. An existing
      // sticky wild receives the same temporary +1 used by the evaluation.
      multiplier: existing === undefined ? 1 : permanentMultiplier + 1,
    }));
  }
  return Object.freeze({
    reel,
    reels: Object.freeze([...new Set(footprint.map((position) => position.reel))].sort((left, right) => left - right)),
    level: event.obeliskLevel ?? 1,
    mode: event.obeliskMode,
    cells: Object.freeze(cells),
  });
}
